<template>
    <div class="scc-container">
        <div class="scc-container__row">
            <div class="scc-ticket-checker-container">
                <div class="scc-ticket-checker">
                    <h1 class="h1">{{ resolvedTitle }}</h1>
                    <template v-if="drawingList && drawingList.length > 0">
                        <div class="scc-ticket-checker__checking-container">
                            <div
                                class="scc-ticket-checker__results"
                                :class="isShowingResults ? 'scc-ticket-checker__results--active' : null"
                            >
                                <label class="bold">{{ resolvedTicketLabel }}</label>
                                <TicketCheckerEntry
                                    v-for="(ticket, index) in tickets"
                                    :key="index"
                                    ref="checkerEntry"
                                    :drawing="drawing"
                                    :numeric-only="numericOnly"
                                    :no-prize-text="noPrizeText"
                                    @won="hasWinningTickets = true"
                                ></TicketCheckerEntry>
                            </div>
                            <div v-if="!isShowingResults" class="scc-ticket-checker__drawing-selector">
                                <label class="bold">{{ drawingLabel }}</label>
                                <SccSelect v-model="selectedDrawingIndex" inverted :options="drawingList" />
                            </div>
                        </div>
                        <span v-if="invalidInput" class="mb-2 note">
                            Er moet tenminste 1 cijfer ingevuld worden om een lot te kunnen checken.
                        </span>

                        <button
                            v-show="tickets.length < max && !isShowingResults"
                            class="scc-ticket-checker__add-more"
                            @click="addHandler"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32.002"
                                height="32.002"
                                viewBox="0 0 32.002 32.002"
                            >
                                <path
                                    id="Subtraction_10"
                                    data-name="Subtraction 10"
                                    d="M22203,13965a16,16,0,1,1,11.311-4.687A15.9,15.9,0,0,1,22203,13965Zm-1.494-15.251v4.517h2.242v-4.517h4.545v-2.236h-4.545V13943h-2.242v4.511H22197v2.236Z"
                                    transform="translate(-22186.998 -13932.999)"
                                    fill="currentColor"
                                />
                            </svg>
                            {{ addButtonText }}
                        </button>

                        <template v-if="!isShowingResults">
                            <span v-if="text" class="scc-ticket-checker__note note">
                                {{ text }}
                            </span>

                            <button
                                class="scc-link scc-link--button scc-link--large scc-link--align-center scc-link--secondary"
                                @click="checkHandler"
                            >
                                {{ checkButtonText }}
                            </button>
                        </template>

                        <template v-else>
                            <span v-if="textResult" class="scc-ticket-checker__note note">
                                {{ textResult }}
                            </span>

                            <button
                                class="scc-link scc-link--button scc-link--large scc-link--align-center scc-link--secondary"
                                @click="resetHandler"
                            >
                                {{ checkMoreButtonText }}
                            </button>
                        </template>
                    </template>

                    <template v-else>
                        <span class="scc-ticket-checker__note">Er zijn nog geen trekkingsuitslagen beschikbaar</span>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import './ticket-checker.scss';
import './scc-link.scss';
import TicketCheckerEntry from './TicketCheckerEntry.vue';
import SccSelect from '../form/SccSelect.vue';
import { format } from 'date-fns';
import localeObject from 'date-fns/locale/nl';

export default {
    name: 'TicketChecker',
    components: { TicketCheckerEntry, SccSelect },
    __editor: {
        requiredData: ['drawings'],
        fields: {
            text: {
                widget: 'textarea',
            },
            textWon: {
                widget: 'textarea',
            },
            textLost: {
                widget: 'textarea',
            },
        },
    },
    props: {
        drawings: {
            type: Array,
            required: true,
        },
        max: {
            type: Number,
            default: 10,
        },
        title: {
            type: String,
            default: 'Trekkingsuitslag',
        },
        titleWon: String,
        titleLost: String,
        text: {
            type: String,
            default:
                'Aan de getoonde resultaten en bedragen kunnen geen rechten worden ontleend. Lees de disclaimer voor meer informatie.',
        },
        textWon: String,
        textLost: String,
        drawingLimit: {
            type: Number,
            default: 5,
        },
        ticketLabel: {
            type: String,
            default: 'Je lotnummer(s)',
        },
        ticketLabelResults: String,
        drawingLabel: {
            type: String,
            default: 'Trekking van',
        },
        addButtonText: {
            type: String,
            default: 'Nog een lot toevoegen',
        },
        checkButtonText: {
            type: String,
            default: 'Nu checken!',
        },
        checkMoreButtonText: {
            type: String,
            default: 'Meer loten checken!',
        },
        noPrizeText: {
            type: String,
            default: 'Helaas, geen prijs',
        },
    },
    data() {
        return {
            tickets: [''],
            isShowingResults: false,
            invalidInput: false,
            selectedDrawingIndex: 0,
            hasWinningTickets: false,
        };
    },
    computed: {
        filteredDrawings() {
            const allDrawings = this.drawings;
            let drawings = allDrawings.filter(drawing => {
                return drawing.status && drawing.status === 'published';
            });
            let sortedDrawings = drawings.sort((a, b) => {
                return new Date(b.publishAt) - new Date(a.publishAt);
            });
            return sortedDrawings || [];
        },
        drawingList() {
            return this.filteredDrawings.slice(0, this.drawingLimit).map((drawing, index) => {
                return {
                    value: index,
                    label: format(new Date(drawing.publishAt), 'MMMM yyyy', { locale: localeObject }),
                };
            });
        },
        drawing() {
            return this.filteredDrawings[this.selectedDrawingIndex];
        },
        numericOnly() {
            const winningTickets = this.drawing?.winningTickets;

            if (winningTickets) {
                winningTickets.forEach(ticket => {
                    if (isNaN(ticket.ticketCode)) {
                        return false;
                    }
                });
            }

            return true;
        },
        textResult() {
            return this.hasWinningTickets ? this.textWon : this.textLost;
        },
        resolvedTitle() {
            if (this.isShowingResults) {
                if (this.hasWinningTickets && this.titleWon) {
                    return this.titleWon;
                } else if (!this.hasWinningTickets && this.titleLost) {
                    return this.titleLost;
                }
            }

            return this.title;
        },
        resolvedTicketLabel() {
            if (this.isShowingResults && this.ticketLabelResults) {
                return this.ticketLabelResults;
            }

            return this.ticketLabel;
        },
    },
    methods: {
        addHandler() {
            this.tickets.push('');
        },
        checkHandler() {
            this.invalidInput = false;
            this.$refs.checkerEntry.forEach(i => {
                if (i.userTicket.length < 1) this.invalidInput = true;
            });
            if (!this.invalidInput) {
                this.$refs.checkerEntry.forEach(i => i.checkTicket());
                this.isShowingResults = true;
            }
        },
        resetHandler() {
            this.tickets = [''];
            this.isShowingResults = false;
            this.$refs.checkerEntry.forEach(i => i.reset());
            this.hasWinningTickets = false;
        },
    },
};
</script>
